import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Loader2, Check, X, Sparkles, Phone, Mail, ArrowRight,
  Rocket, ShoppingBag, Palette, Wand2, Clock,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const PERKS = [
  { icon: ShoppingBag, label: "15 sample products + categories cloned" },
  { icon: Palette, label: "Branding & themes inherited from demo store" },
  { icon: Wand2, label: "Full AI Try-on + Smart Catalog access" },
  { icon: Clock, label: "3 days to evaluate, no card required" },
];

export default function StartStore() {
  const nav = useNavigate();
  const { refresh: refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    slug: "", name: "", owner_phone: "", owner_name: "",
    business_email: "", plan_intent: "pro",
  });
  // True once the user has explicitly edited the URL — we stop auto-deriving
  // it from the brand name from that point on so we don't clobber their choice.
  const [slugEdited, setSlugEdited] = useState(false);
  const [slugAdvanced, setSlugAdvanced] = useState(false);     // show the URL editor
  const [slugStatus, setSlugStatus] = useState({ checking: false, available: null, reason: "" });
  const [otpInput, setOtpInput] = useState("");
  const [sentOtp, setSentOtp] = useState(null);     // demo: backend echoes OTP
  const [submitting, setSubmitting] = useState(false);
  const [trialResult, setTrialResult] = useState(null);

  // Helper — convert "My Shop 2025!" → "my-shop-2025"
  const slugify = (s) =>
    (s || "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 48);

  // Auto-derive the slug from the brand name (unless the user has manually
  // touched the URL field). Runs whenever name changes.
  useEffect(() => {
    if (slugEdited) return;
    const derived = slugify(form.name);
    if (derived !== form.slug) setForm((f) => ({ ...f, slug: derived }));
  }, [form.name, slugEdited]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!form.slug || form.slug.length < 2) {
      setSlugStatus({ checking: false, available: null, reason: "" });
      return;
    }
    let cancelled = false;
    setSlugStatus((s) => ({ ...s, checking: true }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/onboard/start-store/slug-available?slug=${encodeURIComponent(form.slug)}`);
        if (!cancelled) setSlugStatus({ checking: false, available: data.available, reason: data.reason || "" });
      } catch {
        if (!cancelled) setSlugStatus({ checking: false, available: null, reason: "" });
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.slug]);

  const requestOtp = async (e, channel = "sms") => {
    e?.preventDefault();
    if (!form.slug || !form.name || !form.owner_phone) {
      toast.error("Brand name, URL, and phone are required");
      return;
    }
    if (slugStatus.available === false) {
      toast.error("Pick a different URL");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, channel };
      if (!payload.business_email) delete payload.business_email;
      if (!payload.owner_name) delete payload.owner_name;
      const { data } = await api.post("/onboard/start-store", payload);
      setSentOtp(data.otp || null);  // only demo phones echo OTP
      setStep(2);
      const successMsg = data.fallback
        ? "WhatsApp delivery failed — OTP sent via SMS instead."
        : data.channel === "whatsapp"
          ? "OTP sent via WhatsApp."
          : "OTP sent via SMS.";
      toast.success(successMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send OTP");
    } finally { setSubmitting(false); }
  };

  const verifyAndLaunch = async (e) => {
    e?.preventDefault();
    if (!otpInput || otpInput.length < 4) {
      toast.error("Enter the OTP we just sent");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/onboard/start-store/verify", {
        slug: form.slug, owner_phone: form.owner_phone, code: otpInput,
      });
      // Persist auth and drop into /admin
      localStorage.setItem("kb_token", data.token);
      // Stash the new tenant slug so the axios interceptor sends it on every
      // request — otherwise we'd resolve to "default" on the /admin pages.
      localStorage.setItem("kb_tenant", form.slug);
      await refreshUser?.();
      setTrialResult(data);
      setStep(3);
      toast.success("Trial activated — welcome aboard!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not verify OTP");
    } finally { setSubmitting(false); }
  };

  // ---------------- step 3 — success
  if (step === 3 && trialResult) {
    const slug = trialResult.tenant.slug;
    const clone = trialResult.clone || {};
    const cloneSummary = ["categories", "products", "banners", "homepage_sections", "sellers"]
      .map((k) => clone[k] ? `${clone[k]} ${k.replace("_", " ")}` : null)
      .filter(Boolean).join(" · ");

    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-16" data-testid="start-store-done">
        <div className="max-w-2xl w-full space-y-8 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center">
            <Rocket size={36} className="text-green-700" />
          </div>
          <h1 className="font-display text-4xl md:text-5xl">{trialResult.tenant.name} is live.</h1>
          <p className="text-[hsl(var(--kb-muted))] text-lg">
            Your storefront is ready and pre-loaded with demo content so you can see the full experience. You have <b>{trialResult.trial_days} days</b> to explore — upgrade any time to keep going.
          </p>
          {cloneSummary && (
            <div className="bg-[hsl(var(--kb-surface-alt))] border border-[hsl(var(--kb-border))] px-5 py-3 text-sm text-[hsl(var(--kb-muted))]">
              Cloned from our demo store: {cloneSummary}
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <a
              href={`/t/${slug}/`}
              className="kb-btn-outline w-full inline-flex items-center justify-center gap-2"
              data-testid="start-store-view-storefront"
            >
              View your storefront <ArrowRight size={14} />
            </a>
            <button
              type="button"
              onClick={() => nav("/admin")}
              className="kb-btn-primary w-full inline-flex items-center justify-center gap-2"
              data-testid="start-store-open-admin"
            >
              Open admin console <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------- step 2 — OTP
  if (step === 2) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-16" data-testid="start-store-otp">
        <form onSubmit={verifyAndLaunch} className="max-w-md w-full bg-white border border-[hsl(var(--kb-border))] p-8 space-y-5">
          <div className="text-center space-y-2">
            <div className="inline-flex w-12 h-12 rounded-full bg-[hsl(var(--kb-primary))]/10 items-center justify-center">
              <Phone size={20} className="text-[hsl(var(--kb-primary))]" />
            </div>
            <h2 className="font-display text-3xl">Verify your phone</h2>
            <p className="text-sm text-[hsl(var(--kb-muted))]">
              We sent a 6-digit code to <b>+91 {form.owner_phone}</b>
            </p>
          </div>
          {sentOtp && (
            <div className="bg-[hsl(var(--kb-surface-alt))] border border-[hsl(var(--kb-border))] px-3 py-2 text-xs font-mono text-center">
              Demo OTP: {sentOtp}
            </div>
          )}
          <input
            value={otpInput}
            onChange={(e) => setOtpInput(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
            placeholder="6-digit code"
            className="kb-input w-full text-center font-mono text-2xl tracking-[0.5em]"
            data-testid="ss-otp-input"
            autoFocus
          />
          <button type="submit" disabled={submitting} className="kb-btn-primary w-full inline-flex items-center justify-center gap-2 py-3" data-testid="ss-verify">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : "Launch my trial"}
          </button>
          <button type="button" onClick={() => setStep(1)} className="text-xs text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))] block w-full" data-testid="ss-back">
            ← Change phone
          </button>
        </form>
      </div>
    );
  }

  // ---------------- step 1 — form
  return (
    <div className="min-h-screen bg-[hsl(var(--kb-bg))]" data-testid="start-store-page">
      <section className="relative px-6 py-16 md:py-24 max-w-5xl mx-auto">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[hsl(var(--kb-primary))] mb-6">
          <Sparkles size={14} /> 3-day free trial
        </div>
        <h1 className="font-display text-5xl md:text-7xl leading-tight mb-6 max-w-3xl">
          Spin up your <em className="italic">white-label</em> store in 60 seconds.
        </h1>
        <p className="text-lg text-[hsl(var(--kb-muted))] max-w-2xl mb-8">
          No credit card. We&apos;ll clone our demo catalog into your new store so you can see exactly what your customers will see — then start swapping in your own products.
        </p>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3 mb-12">
          {PERKS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} className="bg-white border border-[hsl(var(--kb-border))] p-4 flex items-start gap-3">
                <Icon size={18} className="text-[hsl(var(--kb-primary))] shrink-0 mt-0.5" />
                <div className="text-sm">{p.label}</div>
              </div>
            );
          })}
        </div>
      </section>

      <form onSubmit={(e) => requestOtp(e, "sms")} className="max-w-2xl mx-auto px-6 pb-20 space-y-6">
        <div className="bg-white border border-[hsl(var(--kb-border))] p-6 md:p-8 space-y-5">
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-[hsl(var(--kb-muted))]">Brand name</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Sage & Sienna"
              className="kb-input mt-2 w-full text-lg"
              data-testid="ss-name-input"
              autoFocus
            />
            {form.slug && !slugAdvanced && (
              <div className="mt-2 text-xs text-[hsl(var(--kb-muted))] flex items-center gap-2 flex-wrap" data-testid="ss-url-preview">
                <span>Your URL will be</span>
                <span className="font-mono bg-[hsl(var(--kb-surface-alt))] px-2 py-1 text-[hsl(var(--kb-text))]">
                  getownapp.com/t/<b data-testid="ss-url-slug-text">{form.slug}</b>
                </span>
                <span>
                  {slugStatus.checking && <Loader2 size={12} className="animate-spin inline" />}
                  {!slugStatus.checking && slugStatus.available === true && <Check size={12} className="inline text-green-600" data-testid="ss-slug-ok" />}
                  {!slugStatus.checking && slugStatus.available === false && <X size={12} className="inline text-red-600" data-testid="ss-slug-bad" />}
                </span>
                <button
                  type="button"
                  onClick={() => { setSlugAdvanced(true); setSlugEdited(true); }}
                  className="underline hover:text-[hsl(var(--kb-primary))]"
                  data-testid="ss-customize-url"
                >
                  Customize URL
                </button>
              </div>
            )}
            {slugAdvanced && (
              <div className="mt-3">
                <label className="text-xs font-medium uppercase tracking-wider text-[hsl(var(--kb-muted))]">Custom URL slug</label>
                <div className="flex items-center mt-2 border border-[hsl(var(--kb-border))] focus-within:border-[hsl(var(--kb-primary))]">
                  <span className="px-3 text-sm text-[hsl(var(--kb-muted))] font-mono">getownapp.com/t/</span>
                  <input
                    value={form.slug}
                    onChange={(e) => { setSlugEdited(true); setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }); }}
                    placeholder="my-shop"
                    className="flex-1 py-2 text-sm focus:outline-none font-mono"
                    data-testid="ss-slug-input"
                  />
                  <span className="px-3 text-sm">
                    {slugStatus.checking && <Loader2 size={14} className="animate-spin text-[hsl(var(--kb-muted))]" />}
                    {!slugStatus.checking && slugStatus.available === true && <Check size={14} className="text-green-600" />}
                    {!slugStatus.checking && slugStatus.available === false && <X size={14} className="text-red-600" />}
                  </span>
                </div>
                {!slugStatus.checking && slugStatus.available === false && (
                  <div className="text-xs text-red-600 mt-1">{slugStatus.reason || "That URL is already taken"}</div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-[hsl(var(--kb-muted))] flex items-center gap-1"><Phone size={11} /> Phone</label>
              <input
                value={form.owner_phone}
                onChange={(e) => setForm({ ...form, owner_phone: e.target.value.replace(/[^0-9]/g, "").slice(0, 10) })}
                placeholder="10-digit"
                className="kb-input mt-2 w-full"
                data-testid="ss-phone-input"
              />
              <div className="text-xs text-[hsl(var(--kb-muted))] mt-1">OTP arrives on this number.</div>
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-[hsl(var(--kb-muted))]">Your name (optional)</label>
              <input
                value={form.owner_name}
                onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                placeholder="Your name"
                className="kb-input mt-2 w-full"
                data-testid="ss-owner-name-input"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-[hsl(var(--kb-muted))] flex items-center gap-1"><Mail size={11} /> Business email (optional)</label>
            <input
              type="email"
              value={form.business_email}
              onChange={(e) => setForm({ ...form, business_email: e.target.value })}
              placeholder="hello@brand.com"
              className="kb-input mt-2 w-full"
              data-testid="ss-email-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={(e) => requestOtp(e, "whatsapp")}
            disabled={submitting}
            className="kb-btn-primary w-full text-base py-4 inline-flex items-center justify-center gap-2"
            data-testid="ss-submit-whatsapp"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />}
            WhatsApp OTP
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="kb-btn-secondary w-full text-base py-4 inline-flex items-center justify-center gap-2"
            data-testid="ss-submit"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />}
            SMS OTP
          </button>
        </div>
        <p className="text-center text-xs text-[hsl(var(--kb-muted))]">
          No credit card. Your tenant is provisioned instantly with demo content; upgrade any time before day 4 to keep selling.
        </p>
      </form>
    </div>
  );
}
